import { cn } from '@commenda/utils';
import React, { useState } from 'react';
import { Icons } from '../Icons';
import { InputErrorMessage } from '../InputErrorMessage';

type ElementRenderProps = {
  className: string;
  [key: string]: unknown;
};

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  error?: string;
  showErrorMessage?: boolean;
  leftEl?: (props: ElementRenderProps) => React.ReactNode;
  rightEl?: (props: ElementRenderProps) => React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  type?: string;
  showPasswordToggleIcon?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      error,
      leftEl,
      rightEl,
      showErrorMessage = true,
      fullWidth = false,
      showPasswordToggleIcon = true,
      ...props
    },
    ref
  ) => {
    const baseElementClass =
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5';
    const leftElClass = cn(baseElementClass, 'left-3');
    const rightElClass = cn(baseElementClass, 'right-3');

    const isPasswordInput = type === 'password';
    const [showPassword, setShowPassword] = useState(false);

    const passwordToggleIcon = showPassword ? (
      <Icons.Eye className="size-4 text-fg-quaternary" />
    ) : (
      <Icons.EyeOff className="size-4 text-fg-quaternary" />
    );

    return (
      <div className="w-full flex flex-col gap-2">
        <div className={cn('relative', fullWidth ? 'w-full' : '')}>
          {leftEl && (
            <div className={leftElClass}>
              {leftEl({ className: 'w-full h-full' })}
            </div>
          )}
          <input
            autoComplete="off"
            type={isPasswordInput && showPassword ? 'text' : type}
            className={cn(
              'flex w-full box-border txt-small-regular rounded-sm px-xl py-2.5 bg-bg-primary text-text-primary txt-medium-regular placeholder:text-text-placeholder ring-0 h-10 transition-colors',
              'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled hide-date-icon appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)]',
              leftEl && 'pl-10',
              rightEl && 'pr-9',
              'border disabled:border-border-disabled disabled:pointer-events-none',
              error
                ? 'border-border-error hover:border-border-error-hover focus-visible:border-border-error-hover'
                : 'border-border-primary hover:border-border-primary-hover focus-visible:border-fg-brand',
              className
            )}
            ref={ref}
            {...props}
          />
          {isPasswordInput && showPasswordToggleIcon ? (
            <div
              className={rightElClass}
              onClick={() => setShowPassword(!showPassword)}
              role="button"
              tabIndex={0}
            >
              {passwordToggleIcon}
            </div>
          ) : (
            rightEl && (
              <div className={rightElClass}>
                {rightEl({ className: 'w-full h-full' })}
              </div>
            )
          )}
        </div>
        {error && showErrorMessage ? (
          <InputErrorMessage message={error} />
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
